import {
  ApiRequests,
  ApiRequestsTypes,
  FeatureFlagsContext,
  PlaceHeaderMetadataItem,
  PlacePageActionsMenu,
  PlacePageHeader,
  PlacePageMastheadContainer,
  PlacePageMastheadRowContainer,
  PlacePageTitle,
  PlacePageTitleContainer,
  SiteUrlsContext,
  TableMastheadIcon,
  UIActionsContext,
} from '@tableau/data-catalog-shared';
import { SiteUrls } from '@tableau/shared-urls';
import * as React from 'react';
import { Messages } from '../../../gen-src/Localize';
import { QueryTableOwnAndUpstreamDataQualityWarnings } from '../DataQualityWarnings/DataQualityWarningTables';
import { PlaceHeaderMetadataCertification } from '../placePage/PlaceHeaderMetadataCertification';
import { PlaceHeaderMetadataProject } from '../placePage/PlaceHeaderMetadataProject';
import { PlaceHeaderMetadataTableauUser } from '../placePage/PlaceHeaderMetadataTableauUser';
import { PlacePageIconContainer } from '../placePage/PlacePageIconContainer';
import { TablesDisplayPlacePageData } from '../table/types/TablesDisplayPlacePageData';
import {
  CreateDocumentFromResourceButton,
  RESOURCE_TYPE_TABLE,
} from './CreateWorkbookFromCatalogButtons';

export type TablePlacePageHeaderProps = {
  readonly apiRequests: ApiRequests;
  readonly table: TablesDisplayPlacePageData;
  readonly hideLaunchEditOnMobile?: boolean;
  readonly newFlowFromCatalog?: boolean;
  readonly projectIdForNewDocuments?: string;
};

export function TablePlacePageHeader(
  props: TablePlacePageHeaderProps
): JSX.Element {
  const uiActions = React.useContext(UIActionsContext);
  const siteUrls = React.useContext(SiteUrlsContext);

  const editingIsEnabled: boolean =
    props.apiRequests.isSiteCreatorOrHigher() && !props.hideLaunchEditOnMobile;
  const canCreateFlowFromTable: boolean =
    editingIsEnabled && !!props.newFlowFromCatalog;
  const dbclass: string | null =
    props.table && props.table.database && props.table.database.connectionType;

  return (
    <FeatureFlagsContext.Consumer>
      {
        featureFlags =>
          <PlacePageHeader>
            <PlacePageTitleContainer>
              <PlacePageIconContainer title={Messages.Table()}>
                <TableMastheadIcon />
              </PlacePageIconContainer>
              <PlacePageMastheadContainer>
                <PlacePageMastheadRowContainer>
                  <PlacePageTitle title={props.table.name || undefined}>
                    {props.table.name}
                  </PlacePageTitle>
                  <PlacePageActionsMenu>
                    {uiActions.renderTablesActionMenu(props.table)}
                  </PlacePageActionsMenu>
                </PlacePageMastheadRowContainer>
                <PlacePageMastheadRowContainer>
                  <TableContact table={props.table} />
                  {featureFlags.databasesInProjectsEnabled && <TableProject table={props.table} apiRequests={props.apiRequests} siteUrls={siteUrls} />}
                  <TableCertifier table={props.table} siteUrls={siteUrls} />
                  <TableDataQualityWarningStatusChip table={props.table} />
                </PlacePageMastheadRowContainer>
              </PlacePageMastheadContainer>
            </PlacePageTitleContainer>
            <CreateDocumentFromResourceButton
              apiRequests={props.apiRequests}
              siteUrls={siteUrls}
              isEmbedded={!!props.table.isEmbedded}
              resourceId={props.table.id}
              resourceType={RESOURCE_TYPE_TABLE}
              databaseClass={dbclass}
              testId={'CreateDocumentFromTableButton'}
              title={Messages.NewWorkbookButton()}
              canCreateWorkbook={editingIsEnabled}
              canCreateFlow={canCreateFlowFromTable}
              projectIdForNewDocuments={props.projectIdForNewDocuments}
            />
          </PlacePageHeader>
      }
    </FeatureFlagsContext.Consumer>
  );
}

function TableContact(props: {
  table: TablesDisplayPlacePageData;
}): JSX.Element {
  const contact =
    props.table.__typename === 'DatabaseTable' ? props.table.contact : null;

  return (
    <PlaceHeaderMetadataItem>
      <PlaceHeaderMetadataTableauUser
        tableauUser={contact}
        labelText={Messages.GridHeaderContact()}
      />
    </PlaceHeaderMetadataItem>
  );
}

function TableCertifier(props: {
  table: TablesDisplayPlacePageData;
  siteUrls: SiteUrls;
}): JSX.Element | null {
  if (props.table.__typename !== 'DatabaseTable') {
    return null;
  }

  return (
    <PlaceHeaderMetadataItem>
      <PlaceHeaderMetadataCertification
        anchoredAsset={{
          __typename: 'DataQualityCertification',
          asset: { ...props.table },
        }}
        subjectArea={props.table.__typename}
        vizportalId={props.table.vizportalId}
        contentId={props.table.id}
        certification={props.table}
        siteUrls={props.siteUrls}
        showExtractRefresh={false}
        dataQualityCertifications={props.table.dataQualityCertifications}
      />
    </PlaceHeaderMetadataItem>
  );
}

function TableDataQualityWarningStatusChip(props: {
  table: TablesDisplayPlacePageData;
}): JSX.Element | null {
  if (props.table.__typename !== 'DatabaseTable') {
    return null;
  }

  return (
    <QueryTableOwnAndUpstreamDataQualityWarnings nodeId={props.table.id} />
  );
}

export function TableProject(props: {
  table: TablesDisplayPlacePageData,
  apiRequests: ApiRequests,
  siteUrls: SiteUrls
}): JSX.Element | null {
  const [container, setContainer] = React.useState<ApiRequestsTypes.GetContainerForContentResult>();

  React.useEffect(
    () => {
      props.apiRequests
        .getContainerForContent({ contentType: 'table', contentId: props.table.vizportalId })
        .then(container => setContainer(container))
        .catch(() => setContainer(undefined));
    },
    [props.table, props.apiRequests]
  );

  return (container && container.type === 'PROJECT')
    ? (
      <PlaceHeaderMetadataItem>
        <PlaceHeaderMetadataProject projectId={container.id} projectName={container.name} siteUrls={props.siteUrls} />
      </PlaceHeaderMetadataItem>
    )
    : null;
}
